import { CachedRoomPermissions } from './types'
import { userPermissionsStorage } from './storage'
import { getUser } from '$logic/users'
import { getPv, getPvByUserId } from '$logic/pv'
import { lastRoomListItems } from '$logic/room-list'
import { decodeSubscriptionPermissions } from '$logic/subscription'

async function getBlockedPvsMap(roomIds: string[]) {
  const pvs = await Promise.all(roomIds.map(getPv))
  const userIdToPvRoomIdMap = pvs.reduce(
    (acc, pv) => ({ ...acc, [pv.to]: pv.roomId }),
    {},
  ) as Record<string, string>

  const pvUserIds = Object.keys(userIdToPvRoomIdMap)
  return (await Promise.all(pvUserIds.map(getUser))).reduce(
    (acc, user) => ({
      ...acc,
      [userIdToPvRoomIdMap[user.id]]: !!user.isBlocked,
    }),
    {},
  ) as Record<string, boolean>
}

let roomPointer = 0

export function resetUserPermissions() {
  roomPointer = 0
}

export async function addRoomsToUserPermissionsCache(amount: number) {
  const rooms = lastRoomListItems

  let addedRoomsCount = 0
  const permissionsCache: Record<string, CachedRoomPermissions> = {}

  while (addedRoomsCount < amount && roomPointer < rooms.length) {
    const roomsToAdd = rooms.slice(roomPointer, roomPointer + amount + 1)
    roomPointer = roomPointer + amount + 1

    const blockedPvsMap = await getBlockedPvsMap(
      roomsToAdd
        .filter(({ type }) => type === 'PV')
        .map(({ chatId }) => chatId),
    )

    await Promise.all(
      roomsToAdd.map(async room => {
        if (room.type === 'WAPP_ROOM')
          permissionsCache[room.chatId] = {
            sendMessage: false,
            sendMedia: false,
            sendWapp: false,
          }

        if (room.type === 'PV') {
          const canSendAnyMessage = !blockedPvsMap[room.chatId]
          permissionsCache[room.chatId] = {
            sendMessage: canSendAnyMessage,
            sendMedia: canSendAnyMessage,
            sendWapp: canSendAnyMessage,
          }
        } else if (room.type === 'PRIVATE_SPACE')
          permissionsCache[room.chatId] = {
            sendMessage: true,
            sendMedia: true,
            sendWapp: true,
          }
        else {
          const {
            sendMessage,
            sendMedia,
            sendWapp,
          } = await decodeSubscriptionPermissions(room.chatId)

          permissionsCache[room.chatId] = {
            sendMessage,
            sendMedia,
            sendWapp,
          }
        }

        const { sendMessage, sendMedia, sendWapp } = permissionsCache[
          room.chatId
        ]
        if (sendMessage || sendMedia || sendWapp) addedRoomsCount += 1
      }),
    )

    userPermissionsStorage.set(permissionsCache)
  }

  return permissionsCache
}

export async function updateCachedUserPermissionsInRoom(roomId: string) {
  // NOTE: to update permissions only when forward list is open and initiated
  if (roomPointer === 0) return

  const {
    sendMessage,
    sendMedia,
    sendWapp,
  } = await decodeSubscriptionPermissions(roomId)

  userPermissionsStorage.updateRoom(roomId, {
    sendMessage,
    sendMedia,
    sendWapp,
  })
}

export async function updateCachedUserPermissionsInPv(userId: string) {
  // NOTE: to update permissions only when forward list is open and initiated
  if (roomPointer === 0) return

  const user = await getUser(userId)
  if (!user) return

  const pv = await getPvByUserId(userId)
  if (!pv) return

  const canSendAnyMessage = !user.isBlocked
  userPermissionsStorage.updateRoom(pv.roomId, {
    sendMessage: canSendAnyMessage,
    sendMedia: canSendAnyMessage,
    sendWapp: canSendAnyMessage,
  })
}
